import {useEffect, useRef, useState} from 'react';
import {loadConnectAndInitialize} from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from '@stripe/react-connect-js';
import axios from 'axios';
import {CheckCircle, ExternalLink, XCircle} from 'lucide-react';
import {toast} from 'sonner';
import {useAuth} from '../../modules/auth';
import * as authHelper from '../../modules/auth/core/AuthHelpers';
import {API_ERROR_MESSAGE, TOAST_GENERAL} from '../../Helpers';

import {config} from '../../config';

const BASE_URL = config.apiUrl;
const STRIPE_PUBLISHABLE_KEY = config.stripePublishableKey;

type VerifyStatus = 'idle' | 'verifying' | 'success' | 'timeout';

const StripeConnect = () => {
  const {currentUser, setCurrentUser} = useAuth();
  const isConnected = Number(currentUser?.isStripeConnect) === 1;
  const tokenRef = useRef(currentUser?.token);
  tokenRef.current = currentUser?.token;

  const [stripeConnectInstance] = useState(() =>
    loadConnectAndInitialize({
      publishableKey: STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: async () => {
        const result = await axios.post(
          `${BASE_URL}/payment/stripeAccountSession`,
          {},
          {headers: {Authorization: `Bearer ${tokenRef.current}`}}
        );
        return result.data.data?.client_secret ?? result.data.client_secret;
      },
    })
  );

  const [stripeLoading, setStripeLoading] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>('idle');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [showReportsButton, setShowReportsButton] = useState(isConnected);

  useEffect(() => {
    if (isConnected) setShowReportsButton(true);
  }, [isConnected]);

  const handleViewReports = async () => {
    setReportsLoading(true);
    try {
      const result = await axios.post(
        `${BASE_URL}/payment/stripeLoginLink`,
        {},
        {headers: {Authorization: `Bearer ${tokenRef.current}`}}
      );
      const url = result.data?.data?.url ?? result.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Unable to open reports. Please try again.', {id: TOAST_GENERAL});
      }
    } catch {
      toast.error(API_ERROR_MESSAGE, {id: TOAST_GENERAL});
    } finally {
      setReportsLoading(false);
    }
  };

  const handleOnboardingExit = () => {
    if (!currentUser) return;
    setVerifyStatus('verifying');
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      attempts++;
      try {
        const result = await axios.get(`${BASE_URL}/common/getProfile`, {
          headers: {Authorization: `Bearer ${tokenRef.current}`},
        });
        const profileData = result.data?.data;
        if (profileData?.isStripeConnect) {
          const updatedUser = {...currentUser, ...profileData, isStripeConnect: 1};
          setCurrentUser(updatedUser);
          authHelper.setCurrentUser(updatedUser);
          setVerifyStatus('success');
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setVerifyStatus('timeout');
        }
      } catch {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setVerifyStatus('timeout');
        }
      }
    };

    setTimeout(poll, 1500);
  };

  const renderVerifyStatus = () => {
    if (verifyStatus === 'verifying') {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center py-10 gap-4'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Verifying...</span>
          </div>
          <div className='text-center'>
            <p className='fw-bold fs-5 mb-1'>Verifying your Stripe account</p>
            <p className='text-muted fs-7'>This usually takes a few seconds...</p>
          </div>
        </div>
      );
    }

    if (verifyStatus === 'success') {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center py-10 gap-4'>
          <CheckCircle size={48} className='text-success' />
          <div className='text-center'>
            <p className='fw-bold fs-5 mb-1'>Account connected successfully</p>
            <p className='text-muted fs-7'>Your payment settings are now available above.</p>
          </div>
        </div>
      );
    }

    if (verifyStatus === 'timeout') {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center py-10 gap-4'>
          <XCircle size={48} className='text-warning' />
          <div className='text-center'>
            <p className='fw-bold fs-5 mb-1'>Verification is taking longer than expected</p>
            <p className='text-muted fs-7'>
              Your account may still be processing. Please refresh the page in a moment.
            </p>
          </div>
        </div>
      );
    }

    return null;
  };

  const headerLabel = () => {
    if (verifyStatus === 'success' || isConnected) return 'Payment Settings';
    if (verifyStatus === 'verifying') return 'Connecting Stripe';
    return 'Set Up Stripe Payments';
  };

  const headerSubtitle = () => {
    if (verifyStatus === 'success' || isConnected) return 'Manage your bank account and payout settings';
    if (verifyStatus === 'verifying') return 'Verifying your account with Stripe...';
    return 'Connect your Stripe account to start receiving payments';
  };

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{headerLabel()}</span>
          <span className='text-muted fw-semibold fs-7'>{headerSubtitle()}</span>
        </h3>
        {showReportsButton && (
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleViewReports}
              disabled={reportsLoading}
            >
              {reportsLoading ? (
                <span className='spinner-border spinner-border-sm me-2' role='status' />
              ) : (
                <ExternalLink size={14} className='me-2' />
              )}
              View Stripe Dashboard
            </button>
          </div>
        )}
      </div>
      <div className='card-body'>
        {verifyStatus !== 'idle' ? (
          renderVerifyStatus()
        ) : (
          <>
            {stripeLoading && (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            )}
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              {isConnected ? (
                <>
                  <ConnectNotificationBanner onLoaderStart={() => setStripeLoading(false)} />
                  <ConnectAccountManagement onLoaderStart={() => setStripeLoading(false)} />
                </>
              ) : (
                <ConnectAccountOnboarding
                  onLoaderStart={() => setStripeLoading(false)}
                  onExit={handleOnboardingExit}
                />
              )}
            </ConnectComponentsProvider>
          </>
        )}
      </div>
    </div>
  );
};

export default StripeConnect;
